import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Submission } from '../types';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Plus, FileText } from 'lucide-react';

export const EngineerDashboard: React.FC = () => {
    const { user } = useAuth();
    const [submissions, setSubmissions] = useState<Submission[]>([]);

    useEffect(() => {
        if (user) {
            apiService.getMySubmissions().then(setSubmissions);
        }
    }, [user]);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">My Submissions</h1>
                <Link to="/engineer/submission/new">
                    <Button>
                        <Plus className="mr-2 h-4 w-4" />
                        New Submission
                    </Button>
                </Link>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {submissions.map((submission) => (
                    <Card key={submission.id} className="hover:shadow-md transition-shadow">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-lg flex justify-between items-center">
                                <span>{submission.period}</span>
                                <span className={`text-xs px-2 py-1 rounded-full flex items-center ${submission.status === 'Draft' ? 'bg-gray-100 text-gray-800' :
                                    submission.status === 'Submitted' ? 'bg-blue-100 text-blue-800' :
                                        'bg-green-100 text-green-800'
                                    }`}>
                                    {submission.status === 'Reviewed' && <span className="mr-1">🔒</span>}
                                    {submission.status}
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>Type: {submission.periodType}</p>
                                <p>Skills: {submission.skillCount ?? submission.details.length}</p>
                                <p>
                                    Certificates: {submission.certificateCount ?? submission.details.reduce((total, detail) => total + (detail.certificates?.length || 0), 0)}
                                </p>
                                <p>Last Updated: {new Date(submission.updatedAt).toLocaleDateString()}</p>
                            </div>
                            <div className="mt-4 flex space-x-2">
                                <Link to={`/engineer/submission/${submission.id}`} className="w-full">
                                    <Button variant="outline" className="w-full">
                                        {submission.status === 'Reviewed' ? (
                                            <>
                                                <FileText className="mr-2 h-4 w-4" />
                                                View Review
                                            </>
                                        ) : (
                                            <>
                                                <FileText className="mr-2 h-4 w-4" />
                                                Edit Submission
                                            </>
                                        )}
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {submissions.length === 0 && (
                    <div className="col-span-full text-center py-12 text-gray-500">
                        No submissions found. Create your first one!
                    </div>
                )}
            </div>
        </div>
    );
};
